import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CustomTable from './CustomTable';

const CLIENT_PAGE_SIZE = 12;

const ClientSummary = ({ perClientData = [], granularity }) => {
  const [clientSearch, setClientSearch] = useState('');
  const [displayLimit, setDisplayLimit] = useState(CLIENT_PAGE_SIZE);

  const filteredClients = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    if (!query) return perClientData;
    return perClientData.filter((client) => client.clientName.toLowerCase().includes(query));
  }, [clientSearch, perClientData]);

  useEffect(() => {
    setDisplayLimit(CLIENT_PAGE_SIZE);
  }, [clientSearch, perClientData]);

  const visibleClients = filteredClients.slice(0, displayLimit);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Client Performance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Received, dispatched, cancelled and remaining orders grouped by {granularity.toLowerCase()}.
          </Typography>
        </Box>
        <TextField
          size="small"
          value={clientSearch}
          onChange={(event) => setClientSearch(event.target.value)}
          placeholder="Search clients in results"
          sx={{ width: { xs: '100%', md: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${filteredClients.length} clients`} />
        {clientSearch && <Chip size="small" variant="outlined" label={`Search: ${clientSearch}`} />}
      </Stack>

      {visibleClients.length > 0 ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
            {visibleClients.map((client) => {
              const totals = client.monthlyData.reduce((result, row) => ({
                received: result.received + Number(row.ReceivedOrders || 0),
                dispatched: result.dispatched + Number(row.DispatchedOrders || 0),
                cancelled: result.cancelled + Number(row.CancelledOrders || 0),
                remaining: result.remaining + Number(row.RemainingInProduction || 0),
              }), { received: 0, dispatched: 0, cancelled: 0, remaining: 0 });
              const dispatchRate = totals.received > 0
                ? ((totals.dispatched / totals.received) * 100).toFixed(1)
                : '0.0';

              return (
                <Paper
                  key={client.clientName}
                  variant="outlined"
                  sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, minWidth: 0 }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {client.clientName}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`${totals.received.toLocaleString()} received`} />
                      <Chip size="small" color="success" variant="outlined" label={`${dispatchRate}% dispatched`} />
                      <Chip size="small" color="warning" variant="outlined" label={`${totals.remaining.toLocaleString()} remaining`} />
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(0, 1fr)' },
                      gap: 2,
                      alignItems: 'stretch',
                    }}
                  >
                    <Paper variant="outlined" sx={{ p: 1, minWidth: 0, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, fontWeight: 800 }}>
                        Order trend
                      </Typography>
                      <Box sx={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={client.monthlyData} margin={{ top: 18, right: 12, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" minTickGap={25} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="DispatchedOrders" stackId="orders" fill="#16856a" name="Dispatched" />
                            <Bar dataKey="CancelledOrders" stackId="orders" fill="#c62828" name="Cancelled" />
                            <Bar dataKey="RemainingInProduction" stackId="orders" fill="#1565c0" name="Remaining" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, pb: 1, fontWeight: 800 }}>
                        Order detail
                      </Typography>
                      <CustomTable
                        columns={[
                          { id: 'Period', label: 'Period', minWidth: 140, align: 'left', sortable: true },
                          { id: 'ReceivedOrders', label: 'Received', minWidth: 100, align: 'right', sortable: true },
                          { id: 'DispatchedOrders', label: 'Dispatched', minWidth: 105, align: 'right', sortable: true },
                          { id: 'CancelledOrders', label: 'Cancelled', minWidth: 100, align: 'right', sortable: true },
                          { id: 'RemainingInProduction', label: 'Remaining', minWidth: 100, align: 'right', sortable: true },
                        ]}
                        data={client.monthlyData.map((row, index) => ({
                          id: `${client.clientName}-${row.label}-${index}`,
                          Period: row.label,
                          ReceivedOrders: row.ReceivedOrders,
                          DispatchedOrders: row.DispatchedOrders,
                          CancelledOrders: row.CancelledOrders,
                          RemainingInProduction: row.RemainingInProduction,
                        }))}
                        page={0}
                        rowsPerPage={10}
                        handleChangePage={() => {}}
                        handleChangeRowsPerPage={() => {}}
                        selectable={false}
                      />
                    </Paper>
                  </Box>
                </Paper>
              );
            })}
          </Box>
          {displayLimit < filteredClients.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDisplayLimit((value) => value + CLIENT_PAGE_SIZE)}
              >
                Show more clients
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>No client data found</Typography>
          <Typography variant="body2" color="text.secondary">
            Adjust the shared client or date filters and refresh the report.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ClientSummary;
